import * as React from 'react';
import { useStyles, isDefined } from '@sravni/react-utils';
import { Space } from '../../Space/src/Space.js';
import modules_ef1f9f15 from '../styles/Tabs.module.less.js';

/**
 * @description Use Tabs to organize your content in logical groupings
 *
 * @component
 * @example
 * ```jsx
 * <Tabs activeKey="tab-key">
 *   <Tabs.TabPane key="tab-key" title="Tab">tab content</Tabs.TabPane>
 * </Tabs>
 * ```
 */
const Tabs = React.forwardRef((props, ref) => {
    const { defaultIndex = 0, index, color = 'gray', view = 'badge', className, children, wrap, onChange, ...restProps } = props;
    const innerChildren = React.useMemo(() => React.Children.toArray(children).filter(Boolean), [children]);
    const [innerValue, setInnerValue] = React.useState(index ?? defaultIndex);
    const cx = useStyles(modules_ef1f9f15);
    const classNames = cx('tabs', {
        wrap,
    });
    const handleChangeActiveTab = React.useCallback((event) => {
        const value = Number(event.currentTarget.dataset.index);
        setInnerValue(value);
        onChange?.(value);
    }, [onChange]);
    const navigation = React.useMemo(() => React.Children.map(innerChildren, (child, index) => {
        const classNames = cx('tab', {
            active: index === innerValue,
            // eslint-disable-next-line @typescript-eslint/naming-convention
            'view-text': view === 'text',
            [`color-${color}`]: true,
        });
        if (!React.isValidElement(child)) {
            return null;
        }
        return (React.createElement(Space, { "data-qa": "Tab", size: 4, align: "center", inline: true, className: classNames, "data-index": index, onClick: handleChangeActiveTab }, child.props.title));
    }), [innerChildren, cx, innerValue, view, color, handleChangeActiveTab]);
    const panes = React.useMemo(() => React.Children.map(innerChildren, (child, index) => {
        if (!React.isValidElement(child)) {
            return null;
        }
        const classNames = cx(child.props.className, 'pane', {
            active: index === innerValue,
        });
        return React.cloneElement(child, { ...child.props, className: classNames });
    }), [innerChildren, cx, innerValue]);
    React.useEffect(() => {
        if (!isDefined(index)) {
            return;
        }
        setInnerValue(index);
    }, [index]);
    return (React.createElement("div", { ref: ref, "data-qa": "Tabs", className: className, ...restProps },
        React.createElement(Space, { size: view === 'text' ? 16 : 12, align: "center", className: cx(classNames) }, navigation),
        React.createElement("div", { className: cx('panes') }, panes)));
});
Tabs.displayName = 'Tabs';

export { Tabs };
//# sourceMappingURL=Tabs.js.map
